import { Game } from "../../core/Game";
import { canPickUpMachine } from "../../game/game-actions/machine-actions";
import { heldTool, holdingBroom } from "../../game/HeldTool";
import {
  InteractFacts,
  interactRing,
  resolveInteract,
} from "../../game/interact";
import { atTruckBed } from "../../game/lot";
import { hasFloorControls, isSameMachine, Machine } from "../../game/Machine";
import {
  findFeedableOperation,
  liveSettingParameter,
  parameterValueSatisfiable,
  slideStock,
  stageableMaterials,
} from "../../game/machine-helpers";
import { MaterialInstance } from "../../game/Materials";
import { handSpaceLeft } from "../../game/Person";
import { carryingShopVac } from "../../game/ShopVac";
import { atStand, isSellable } from "../../game/stand";
import { availableOperations } from "../../game/skill-helpers";
import { chebyshevDistance } from "../../game/Vectors";
import { MachineCrateEntity } from "../entities/MachineCrateEntity";
import { MachineEntity } from "../entities/MachineEntity";
import { MaterialPileEntity } from "../entities/MaterialPileEntity";
import { Player } from "../entities/Player";
import { StandEntity } from "../entities/StandEntity";
import { TruckEntity } from "../entities/TruckEntity";
import { projectPerson, projectProgression } from "../projection";
import { ShopInfo } from "../singletons/ShopInfo";
import {
  cleaningGear,
  grabShopVac,
  parkShopVac,
  pickUpBroom,
  putDownBroom,
} from "./cleaning-commands";
import {
  carryFacts,
  findMachineEntity,
  shopCellMap,
  moveMaterialsToMachine,
  operateMachine,
  pickUpCrate,
  pickUpMachine,
  putDownCarriedMachine,
  setMachineOperation,
  setMachineSettings,
  takeInputsFromMachine,
  takeOutputsFromMachine,
  toggleMachinePower,
} from "./machine-commands";
import { dropMaterial, pickUpMaterial } from "./pile-commands";
import { setOutAtStand, setUpStand, takeFromStand } from "./stand-commands";
import {
  takeCrateFromTruck,
  takeFromTruckBed,
  loadTruckBed,
} from "./truck-commands";

/**
 * The composite interaction commands — the old ShopKeyboardShortcuts
 * handler bodies, rehosted. The dispatcher stays logic-free (the plan's
 * command-layer rule): each of these owns one key's whole decision and
 * calls the underlying commands; the driver and the hint chips share the
 * same resolvers, so the key never surprises.
 *
 * "What the key acts on" (the targeted machine, the rummage cursor)
 * arrives as arguments — targeting is shell state, not sim state.
 */

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** The old util's modulo, local so the sim owns its arithmetic. */

function player(game: Game): Player {
  return game.entities.getSingleton(Player);
}

/** The interact resolvers' read, off the live entities. */
export function interactFacts(game: Game): InteractFacts {
  const stand = game.entities.tryGetSingleton(StandEntity);
  return {
    ...cleaningGear(game),
    player: projectPerson(game),
    materialPiles: [...game.entities.byConstructor(MaterialPileEntity)]
      .filter((entity) => !entity.isDestroyed)
      .map((entity) => entity.pile),
    truck: (({ bed, crates }) => ({ bed, crates }))(
      game.entities.getSingleton(TruckEntity),
    ),
    stand: stand?.pieces ?? [],
    standSetUp: stand?.setUp ?? false,
    progression: projectProgression(game),
    shopInfo: game.entities.getSingleton(ShopInfo).info,
  };
}

/**
 * The B key's three-way toggle: put down what's carried, else unpack the
 * crate underfoot (or at the truck's bed), else hoist the targeted
 * machine.
 */
export function carryMachineToggle(
  game: Game,
  targeted: MachineEntity | null,
): void {
  const thePlayer = player(game);
  if (thePlayer.carriedMachine) {
    putDownCarriedMachine(game);
    return;
  }
  const crateUnderfoot = [
    ...game.entities.byConstructor(MachineCrateEntity),
  ].some(
    (crate) =>
      !crate.isDestroyed &&
      chebyshevDistance(crate.position, thePlayer.cell) <= 1,
  );
  if (crateUnderfoot) {
    pickUpCrate(game);
    return;
  }
  const shopInfo = game.entities.getSingleton(ShopInfo).info;
  const truck = game.entities.getSingleton(TruckEntity);
  if (truck.crates.length > 0 && atTruckBed(shopInfo, thePlayer.cell)) {
    takeCrateFromTruck(game);
    return;
  }
  if (targeted && canPickUpMachine(carryFacts(game), targeted.state)) {
    pickUpMachine(game, targeted);
  }
}

/**
 * The E key: take finished work, unload a bay, switch the machine on,
 * pick up the floor, take from the bed or the stand — whichever the
 * shared resolver says applies here. Returns "truck-cab" when the press
 * should open the trip card (shell state, so the dispatcher owns it).
 */
export function interactHere(
  game: Game,
  targeted: MachineEntity | null,
  pileOffset: number,
  shift: boolean,
): "truck-cab" | "open-station" | null {
  const gs = interactFacts(game);
  const targetedView = targeted ? targeted.view() : undefined;
  let action = resolveInteract(gs, shopCellMap(game), targetedView, pileOffset);
  // Shift means "take everything", never "use": with the outline on the
  // bench itself, Shift+E sweeps up its stock and the floor's pieces
  // from the first take on the ring instead of leaning in.
  if (shift && action?.kind === "open-station") {
    const ring = interactRing(gs, shopCellMap(game), targetedView);
    const firstTake = ring.findIndex(
      (entry) => entry.kind !== "open-station" && entry.kind !== "switch-on",
    );
    if (firstTake !== -1) {
      action = resolveInteract(gs, shopCellMap(game), targetedView, firstTake);
    }
  }
  if (!action) return null;
  const space = handSpaceLeft(gs.player);
  switch (action.kind) {
    // Leaning over a bench and spreading a sheet are shell state, not
    // sim state: the dispatcher opens whichever the station has.
    case "open-station":
      return "open-station";
    case "take-outputs": {
      const entity = findMachineEntity(game, action.machine.state);
      if (!entity) return null;
      takeOutputsFromMachine(
        game,
        armfulFrom(
          action.machine.outputMaterials,
          action.material,
          shift,
          space,
        ),
        entity,
      );
      return null;
    }
    case "take-inputs": {
      const entity = findMachineEntity(game, action.machine.state);
      if (!entity) return null;
      takeInputsFromMachine(
        game,
        armfulFrom(
          action.machine.inputMaterials,
          action.material,
          shift,
          space,
        ),
        entity,
      );
      return null;
    }
    case "switch-on":
    case "switch-off": {
      const entity = findMachineEntity(game, action.machine.state);
      if (entity) toggleMachinePower(game, entity);
      return null;
    }
    case "pick-up-floor": {
      // Grab the piece R rummaged to (the top of the pile by default).
      // Shift's armful starts there too and wraps around the pile. The
      // resolver hands back pile *data*; map to the live entities by the
      // material's id (unique per instance).
      const piles = action.piles;
      const from = piles.indexOf(action.target);
      const wanted = shift
        ? [...piles.slice(from), ...piles.slice(0, from)].slice(0, space)
        : [action.target];
      const entities = [...game.entities.byConstructor(MaterialPileEntity)];
      const targets = wanted
        .map((pile) =>
          entities.find((entity) => entity.material.id === pile.material.id),
        )
        .filter((entity): entity is MaterialPileEntity => entity != null);
      if (targets.length > 0) pickUpMaterial(game, targets);
      return null;
    }
    case "pick-up-broom":
      pickUpBroom(game);
      return null;
    case "pick-up-vac":
      grabShopVac(game);
      return null;
    case "truck-bed": {
      const bed = gs.truck.bed;
      takeFromTruckBed(game, shift ? bed.slice(-space) : [bed[bed.length - 1]]);
      return null;
    }
    case "stand":
      takeFromStand(game, shift ? space : 1);
      return null;
    case "set-up-stand":
      setUpStand(game);
      return null;
    case "truck-cab":
      return "truck-cab";
  }
  return null;
}

/**
 * The pieces a take lifts off a machine: the one the outline is on, or
 * with Shift an armful starting there and wrapping around the rest —
 * the same shape a floor pickup has.
 */
function armfulFrom(
  staged: ReadonlyArray<MaterialInstance>,
  target: MaterialInstance,
  shift: boolean,
  space: number,
): MaterialInstance[] {
  const from = Math.max(
    0,
    staged.findIndex((material) => material.id === target.id),
  );
  return shift
    ? [...staged.slice(from), ...staged.slice(0, from)].slice(0, space)
    : [staged[from]];
}

/**
 * The F key: hand stock to the targeted machine if it takes it, load the
 * truck's bed, set sellable work out at the stand, lean the broom, park
 * the vac, or set the piece down at the body's feet — the old put-down
 * handler.
 */
export function putDownHere(
  game: Game,
  targeted: MachineEntity | null,
  shift: boolean,
): void {
  const thePlayer = player(game);
  const shopInfo = game.entities.getSingleton(ShopInfo).info;
  const inventory = thePlayer.inventory;
  if (inventory.length === 0) {
    // Empty-handed except for a tool: F leans the broom right here, or
    // parks the vac
    const gear = cleaningGear(game);
    if (holdingBroom(gear)) putDownBroom(game);
    else if (carryingShopVac(gear)) parkShopVac(game);
    return;
  }

  // At the truck's bed, F loads over the rail instead of dropping
  if (atTruckBed(shopInfo, thePlayer.cell)) {
    loadTruckBed(game, shift ? [...inventory] : [inventory[0]]);
    return;
  }

  // At the for-sale stand, F sets sellable work out on the table
  if (
    game.entities.getSingleton(StandEntity).setUp &&
    atStand(shopInfo, thePlayer.cell)
  ) {
    const sellable = inventory.filter(isSellable);
    if (sellable.length > 0) {
      setOutAtStand(game, shift ? sellable : [sellable[0]]);
      return;
    }
  }

  if (targeted && targeted.state.operationProgress.status !== "inProgress") {
    const stageable = stageableMaterials(
      targeted.view(),
      inventory,
      projectProgression(game),
    );
    if (stageable.length > 0) {
      const staged = shift ? stageable : [stageable[0]];
      moveMaterialsToMachine(game, staged, targeted);
      // A power-feed machine has no separate trigger: the rollers grab
      // the board the moment it touches them, so setting it down *is*
      // starting it. (Read from the post-stage state.)
      if (targeted.type.directFeed) {
        const restaged = targeted.view();
        const match = findFeedableOperation(
          restaged,
          availableOperations(restaged, projectProgression(game)),
          restaged.inputMaterials,
        );
        if (match?.operation.powerFeed === true) {
          operateMachine(game, targeted);
        }
      }
      return;
    }
  }

  setDownAtFeet(game, shift ? [...inventory] : [inventory[0]]);
}

/**
 * Set held pieces down at the body's feet — putDownHere's last resort,
 * and the hands strip's whole verb: a slot's click means "set this
 * down", even standing at a machine, where F would stage instead (the
 * strip is how you park a spare without feeding it).
 *
 * The pieces land at the body's actual position and keep the
 * orientation they were carried in — the person sprite draws a quarter
 * turn off the heading, so the same offset lays a piece down exactly
 * as it looked in the arms. `heading` is the body's continuous facing
 * (unlike the quantized `direction`), so a piece dropped mid-diagonal
 * keeps that angle instead of snapping to one of four.
 */
export function setDownAtFeet(
  game: Game,
  materials: ReadonlyArray<MaterialInstance>,
): void {
  if (materials.length === 0) return;
  const thePlayer = player(game);
  dropMaterial(
    game,
    [...materials],
    [...thePlayer.position],
    thePlayer.heading + Math.PI / 2,
  );
}

/**
 * The held operate key starting the targeted machine — refused while a
 * tool in hand owns the hold (sweeping runs off the held flag).
 */
export function operateTargeted(
  game: Game,
  targeted: MachineEntity | null,
): void {
  if (heldTool(cleaningGear(game)) !== null) return;
  if (!targeted) return;
  operateMachine(game, targeted);
}

/**
 * Step one of the machine's settings — the keyboard equivalent of the
 * scales on its card, ported from the old stepSetting with the slide
 * reachability walk.
 */
export function stepMachineSetting(
  game: Game,
  targeted: MachineEntity | null,
  kind: "linear" | "rotate",
  step: 1 | -1,
  coarse = false,
): void {
  if (!targeted) return;
  const machine = targeted.view();

  const directFeed = machine.type.directFeed === true;
  const found = liveSettingParameter(machine, projectProgression(game), kind);
  if (!found) return;
  const { operation, parameter: param } = found;

  // Unset reads as the declared default — what the chip already shows —
  // so the first press steps off it instead of jumping to the scale's
  // first mark.
  const current = machine.selectedParameters?.[param.id] ?? param.defaultValue;
  const currentIndex =
    current === undefined ? -1 : param.values.indexOf(current);
  const detents = coarse ? (param.coarseStep ?? 1) : 1;
  let next =
    param.values[mod(currentIndex + step * detents, param.values.length)];

  // A slide param moves the stock itself, so the key steps between the
  // marks the stock can actually reach.
  if (param.presentation === "slide") {
    const stock = slideStock(machine, [operation]);
    let nextIndex = param.values.indexOf(next);
    for (
      let tries = 0;
      tries < param.values.length &&
      !parameterValueSatisfiable(
        machine,
        operation,
        param.id,
        param.values[nextIndex],
        stock ? [stock] : [],
      );
      tries++
    ) {
      nextIndex = mod(nextIndex + step, param.values.length);
    }
    next = param.values[nextIndex];
  }

  if (directFeed) {
    // Settings turn without touching what's selected or running
    setMachineSettings(game, targeted, { [param.id]: next });
  } else {
    setMachineOperation(game, targeted, operation, {
      ...machine.selectedParameters,
      [param.id]: next,
    });
  }
}
